import { useInvestorDropdown } from "@/application/workflows/investors/index";
import type { InvestorSummary } from "@/domain/investor";
import { useCallback } from "react";
import type { InvestmentResponse } from "@/domain/investment";

interface UseInvestorDropdownStateParams {
  isOpen: boolean;
  preselectedInvestor: InvestorSummary | null;
  existingInvestment: InvestmentResponse | null;
  setSelectedInvestor: (investor: InvestorSummary | null) => void;
}

export function useInvestorDropdownState({
  isOpen,
  preselectedInvestor,
  existingInvestment,
  setSelectedInvestor,
}: UseInvestorDropdownStateParams) {
  const investorPreselection =
    preselectedInvestor ??
    (existingInvestment?.investor
      ? {
          uuid: existingInvestment.investor.uuid,
          display_name: existingInvestment.investor.display_name,
          type: existingInvestment.investor.type,
        }
      : null);

  const dropdown = useInvestorDropdown({
    enabled: isOpen,
    preSelectedItem: investorPreselection,
  });

  const handleInvestorChange = useCallback(
    (investorId: string) => {
      if (!investorId) {
        setSelectedInvestor(null);
        return;
      }
      const fullInvestor = dropdown.getInvestorByUuid(investorId);
      if (fullInvestor) {
        setSelectedInvestor({
          uuid: fullInvestor.uuid,
          type: fullInvestor.type,
          status: fullInvestor.status,
          display_name: fullInvestor.display_name,
          email: fullInvestor.email,
          tax_identifier: fullInvestor.tax_identifier,
          city: fullInvestor.city,
        });
      }
    },
    [dropdown, setSelectedInvestor],
  );

  return {
    investorOptions: dropdown.options,
    loadingInvestorsDropdown: dropdown.loading,
    loadingMoreInvestors: dropdown.loadingMore,
    hasMoreInvestors: dropdown.hasMore,
    handleInvestorSearch: dropdown.handleSearch,
    handleLoadMoreInvestors: dropdown.handleLoadMore,
    handleInvestorChange,
  };
}
